import React from 'react';
import {
  Box,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { MetricCard, RiskChip } from './AdminUi';
import {
  formatInteger,
  formatRelativeTime,
  formatTokenBalance,
} from './utils';

const OverviewTab = ({
  loadingSettings,
  settings,
  loadingTenants,
  tenantSnapshot,
}) => {
  if (loadingSettings || !settings) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>Loading overview...</Typography>
      </Box>
    );
  }

  const userLimit = settings.counts?.userLimit;
  const limitDescription = userLimit
    ? `${settings.counts?.users || 0} of ${userLimit} seats used`
    : `${settings.counts?.users || 0} users active`;

  return (
    <Stack spacing={3}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <MetricCard
            title="Registered users"
            value={formatInteger(settings.counts?.users)}
            description={limitDescription}
            icon={<CheckCircleIcon color="primary" />}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            title="Introductory cases"
            value={formatTokenBalance(settings.billing?.freeSignupTokens ?? 0)}
            description="Granted on new individual sign-up"
            icon={<CheckCircleIcon color="success" />}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            title="Tokens per history"
            value={formatTokenBalance(settings.billing?.historyCostTokens ?? 1)}
            description="Deducted for platform-funded histories"
            icon={<WarningAmberIcon color="warning" />}
          />
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Recent activity</Typography>
        {loadingTenants ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Stack spacing={2}>
            {(tenantSnapshot?.tenants || []).slice(0, 5).map((tenant) => (
              <Stack key={tenant.id} direction="row" spacing={2} alignItems="center">
                <Typography sx={{ fontWeight: 600, minWidth: 220 }}>{tenant.name}</Typography>
                <RiskChip riskLevel={tenant.riskLevel} />
                <Typography variant="body2" color="text.secondary">
                  {tenant.aiFundingMode === 'tenant_byok'
                    ? 'AI billing: University managed (BYOK)'
                    : `Wallet balance: ${formatTokenBalance(tenant.walletBalance ?? 0)} credits`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Last activity: {tenant.history?.lastActivity
                    ? formatRelativeTime(tenant.history.lastActivity)
                    : 'No submissions'}
                </Typography>
              </Stack>
            ))}
            {!(tenantSnapshot?.tenants || []).length && (
              <Typography variant="body2" color="text.secondary">
                No tenants found yet. Create your first university to get started.
              </Typography>
            )}
          </Stack>
        )}
      </Paper>
    </Stack>
  );
};

export default OverviewTab;
